import React, { useState, useEffect } from 'react';
import { 
  BarChart3, Users, CreditCard, Building2, FileText, Settings, 
  TrendingUp, DollarSign, Calendar, Bell, Search, Filter,
  ArrowUpRight, ArrowDownRight, Eye, Edit, Trash2, Plus
} from 'lucide-react';
import { Link } from 'react-router-dom';

interface DashboardStats {
  totalApplications: number;
  loanApplications: number;
  cardApplications: number;
  depositApplications: number;
  remoteCustomerApplications: number;
  todayApplications: number;
  weeklyGrowth: number;
  monthlyRevenue: number;
}

interface RecentApplication {
  id: string;
  type: string;
  customerName: string;
  amount?: string;
  status: 'pending' | 'approved' | 'rejected';
  createdAt: string;
}

interface BankLogo {
  id: string;
  name: string;
  logo: string;
}

const Dashboard = () => {
  const [stats, setStats] = useState<DashboardStats>({
    totalApplications: 1247,
    loanApplications: 523,
    cardApplications: 389,
    depositApplications: 201,
    remoteCustomerApplications: 134,
    todayApplications: 23,
    weeklyGrowth: 12.5,
    monthlyRevenue: 45600
  });

  const [recentApplications, setRecentApplications] = useState<RecentApplication[]>([
    {
      id: '1',
      type: 'İhtiyaç Kredisi',
      customerName: 'Ahmet Yılmaz',
      amount: '50.000 TL',
      status: 'pending',
      createdAt: '2024-01-18T10:30:00Z'
    },
    {
      id: '2',
      type: 'Kredi Kartı',
      customerName: 'Ayşe Demir',
      status: 'approved',
      createdAt: '2024-01-18T09:15:00Z'
    },
    {
      id: '3',
      type: 'Mevduat',
      customerName: 'Mehmet Kaya',
      amount: '100.000 TL',
      status: 'pending',
      createdAt: '2024-01-18T08:45:00Z'
    }
  ]);

  const [bankLogos, setBankLogos] = useState<BankLogo[]>([
    {
      id: 'akbank',
      name: 'Akbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/a80c0972-6830-4dc4-b3bc-e236c7fbb5ea.png'
    },
    {
      id: 'garanti',
      name: 'Garanti BBVA',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2c60a02b-d37c-48ce-ad8e-deb4e57bf810.jpg'
    },
    {
      id: 'yapikredi',
      name: 'Yapı Kredi',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/52f6626d-498d-4e76-8fb5-fd4381a1e0db.png'
    },
    {
      id: 'qnb',
      name: 'QNB Finansbank',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/d9ae3539-f63d-4295-84e6-08dbe6e2a6d6.png'
    },
    {
      id: 'isbankasi',
      name: 'İş Bankası',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/3b159da8-fe94-4b12-b9fa-4b9c60495305.png'
    },
    {
      id: 'ziraatbankasi',
      name: 'Ziraat Bankası',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/2cf1c9fe-fbff-4874-a365-fd63455d078c.png'
    },
    {
      id: 'nkolay',
      name: 'Nkolay',
      logo: 'https://cdn.hangikredi.com/images/bankdigitalonboarding/7accd65f-06ae-48b1-875e-6d9827590be6.png'
    }
  ]);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'approved': return 'text-green-600 bg-green-100';
      case 'rejected': return 'text-red-600 bg-red-100';
      default: return 'text-yellow-600 bg-yellow-100';
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'approved': return 'Onaylandı';
      case 'rejected': return 'Reddedildi';
      default: return 'Beklemede';
    }
  };

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header - Fixed at very top */}
      <div className="sticky top-0 z-20 bg-white border-b border-slate-200">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-slate-900">Admin Dashboard</h1>
              <p className="text-slate-600">KrediPark Yönetim Paneli</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400" size={20} />
                <input
                  type="text"
                  placeholder="Ara..."
                  className="pl-10 pr-4 py-2 w-64 border border-slate-200 rounded-lg focus:ring-2 focus:ring-[#F7BA45] focus:border-[#F7BA45]"
                />
              </div>
              <button className="relative p-2 text-slate-600 hover:text-slate-900 hover:bg-slate-100 rounded-lg transition-colors">
                <Bell size={20} />
                <span className="absolute top-1 right-1 w-2 h-2 bg-red-500 rounded-full"></span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content Layout - Both sidebars start from same level */}
      <div className="flex">
        {/* Left Content Area */}
        <div className="flex-1 p-6">
          {/* Stats Cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 mb-6 sm:mb-8">
            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600 mb-1">Toplam Başvuru</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.totalApplications.toLocaleString()}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <ArrowUpRight size={16} className="text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-600">+{stats.weeklyGrowth}%</span>
                    <span className="text-sm text-slate-500">bu hafta</span>
                  </div>
                </div>
                <div className="bg-blue-100 p-3 rounded-lg flex-shrink-0">
                  <FileText className="text-blue-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600 mb-1">Kredi Başvuruları</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.loanApplications}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <ArrowUpRight size={16} className="text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-600">+8.2%</span>
                    <span className="text-sm text-slate-500">bu hafta</span>
                  </div>
                </div>
                <div className="bg-green-100 p-3 rounded-lg flex-shrink-0">
                  <DollarSign className="text-green-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600 mb-1">Kart Başvuruları</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.cardApplications}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <ArrowUpRight size={16} className="text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-600">+15.3%</span>
                    <span className="text-sm text-slate-500">bu hafta</span>
                  </div>
                </div>
                <div className="bg-purple-100 p-3 rounded-lg flex-shrink-0">
                  <CreditCard className="text-purple-600" size={24} />
                </div>
              </div>
            </div>

            <div className="bg-white rounded-xl p-4 sm:p-6 border border-slate-200 shadow-sm">
              <div className="flex items-center justify-between">
                <div className="min-w-0 flex-1">
                  <p className="text-sm text-slate-600 mb-1">Bugünkü Başvurular</p>
                  <p className="text-xl sm:text-2xl font-bold text-slate-900">{stats.todayApplications}</p>
                  <div className="flex items-center gap-1 mt-2">
                    <ArrowUpRight size={16} className="text-green-600 flex-shrink-0" />
                    <span className="text-sm text-green-600">+23</span>
                    <span className="text-sm text-slate-500">dün</span>
                  </div>
                </div>
                <div className="bg-orange-100 p-3 rounded-lg flex-shrink-0">
                  <Calendar className="text-orange-600" size={24} />
                </div>
              </div>
            </div>
          </div>

          {/* Bank Logos Section */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm mb-6 sm:mb-8">
            <div className="p-4 sm:p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Banka Logoları</h2>
                <Link 
                  to="/admin/content/banks"
                  className="text-[#F7BA45] hover:text-[#e5ac3d] text-sm font-medium"
                >
                  Banka Yönetimi
                </Link>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-7 gap-4">
                {bankLogos.map((bank) => (
                  <div key={bank.id} className="flex flex-col items-center p-4 bg-slate-50 rounded-lg hover:bg-slate-100 transition-colors">
                    <img 
                      src={bank.logo} 
                      alt={bank.name}
                      className="h-10 object-contain mb-2"
                    />
                    <span className="text-sm text-slate-700 text-center">{bank.name}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Recent Applications */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
            <div className="p-4 sm:p-6 border-b border-slate-200">
              <div className="flex items-center justify-between">
                <h2 className="text-lg font-semibold text-slate-900">Son Başvurular</h2>
                <Link 
                  to="/admin/applications"
                  className="text-[#F7BA45] hover:text-[#e5ac3d] text-sm font-medium"
                >
                  Tümünü Gör
                </Link>
              </div>
            </div>
            <div className="p-4 sm:p-6">
              <div className="space-y-4">
                {recentApplications.map((application) => (
                  <div key={application.id} className="flex items-center justify-between p-4 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-4 min-w-0 flex-1">
                      <div className="w-10 h-10 bg-[#F7BA45]/10 rounded-lg flex items-center justify-center flex-shrink-0">
                        <FileText size={20} className="text-[#F7BA45]" />
                      </div>
                      <div className="min-w-0 flex-1">
                        <p className="font-medium text-slate-900 truncate">{application.customerName}</p>
                        <p className="text-sm text-slate-600 truncate">{application.type}</p>
                        {application.amount && (
                          <p className="text-sm text-slate-500">{application.amount}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-3 flex-shrink-0">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(application.status)}`}>
                        {getStatusText(application.status)}
                      </span>
                      <div className="flex items-center gap-1">
                        <button className="p-1 text-slate-400 hover:text-slate-600">
                          <Eye size={16} />
                        </button>
                        <button className="p-1 text-slate-400 hover:text-slate-600">
                          <Edit size={16} />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Right Sidebar - Starts from same level as left sidebar */}
        <div className="w-80 flex-shrink-0">
          <div className="p-6 space-y-6">
            {/* Quick Actions */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Hızlı İşlemler</h2>
              </div>
              <div className="p-4 sm:p-6 space-y-3">
                <Link 
                  to="/admin/content/banks"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Building2 size={20} className="text-[#F7BA45] flex-shrink-0" />
                  <span className="text-slate-700">Banka Yönetimi</span>
                </Link>
                <Link 
                  to="/admin/content/products"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <CreditCard size={20} className="text-[#F7BA45] flex-shrink-0" />
                  <span className="text-slate-700">Ürün Yönetimi</span>
                </Link>
                <Link 
                  to="/admin/content/campaigns"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <TrendingUp size={20} className="text-[#F7BA45] flex-shrink-0" />
                  <span className="text-slate-700">Kampanya Yönetimi</span>
                </Link>
                <Link 
                  to="/admin/content/pages"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <FileText size={20} className="text-[#F7BA45] flex-shrink-0" />
                  <span className="text-slate-700">Sayfa İçerikleri</span>
                </Link>
                <Link 
                  to="/admin/settings"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <Settings size={20} className="text-[#F7BA45] flex-shrink-0" />
                  <span className="text-slate-700">Sistem Ayarları</span>
                </Link>
              </div>
            </div>

            {/* Application Types Chart */}
            <div className="bg-white rounded-xl border border-slate-200 shadow-sm">
              <div className="p-4 sm:p-6 border-b border-slate-200">
                <h2 className="text-lg font-semibold text-slate-900">Başvuru Türleri</h2>
              </div>
              <div className="p-4 sm:p-6">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Kredi Başvuruları</span>
                    <span className="text-sm font-medium text-slate-900">{stats.loanApplications}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-green-500 h-2 rounded-full transition-all duration-500" style={{ width: '42%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Kart Başvuruları</span>
                    <span className="text-sm font-medium text-slate-900">{stats.cardApplications}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-purple-500 h-2 rounded-full transition-all duration-500" style={{ width: '31%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Mevduat</span>
                    <span className="text-sm font-medium text-slate-900">{stats.depositApplications}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-blue-500 h-2 rounded-full transition-all duration-500" style={{ width: '16%' }}></div>
                  </div>
                  
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-slate-600">Müşteri Edinimi</span>
                    <span className="text-sm font-medium text-slate-900">{stats.remoteCustomerApplications}</span>
                  </div>
                  <div className="w-full bg-slate-200 rounded-full h-2">
                    <div className="bg-orange-500 h-2 rounded-full transition-all duration-500" style={{ width: '11%' }}></div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;